import { t } from "@web-monorepo/i18n/macro";
import isMobile from "@web-monorepo/utilities/src/mobile";
import { DDSDisplayText } from "@web-monorepo/dds";
import { TranslatedString } from "@web-monorepo/translation-runtime";
import { clsx } from "clsx";
import FocusTrap from "focus-trap-react";
import React from "react";
import MaterialIcon from "./MaterialIcon";
import { useAppPageBack } from "./useAppPageBack";
import styles from "./AppPage.module.css";

/**
 * Identifier for each toolkit pod that uses an AppPage frame on the
 * teacher's remote. Stylistic labels, not status indicators — `groups` is red
 * because the groups tool's brand is red, not because something is dangerous.
 * Pods that share a color (list/random, pairshare/timer) still get their own
 * variant so future recoloring is independent.
 */
export type AppPageVariant = "groups" | "list" | "random" | "pairshare" | "timer" | "noise" | "radio" | "today";

interface AppPageProps {
  variant: AppPageVariant;
  title: TranslatedString;
  icon?: string;
  classId?: string;
  mountPoint?: string;
  children: React.ReactNode;
  /**
   * Necessary when rendering an AppPage while another is already on screen
   * (e.g. modal stacks): the outer FocusTrap would otherwise block focus on
   * the new instance.
   */
  disableFocusTrap?: boolean;
}

const AppPage = ({ variant, title, icon, classId, mountPoint, children, disableFocusTrap }: AppPageProps) => {
  const handleBack = useAppPageBack(mountPoint, classId);
  return (
    <FocusTrap
      active={!isMobile.any && !disableFocusTrap}
      focusTrapOptions={{
        fallbackFocus: () => document.querySelector("[data-app-page-container]") as HTMLElement,
        allowOutsideClick: true,
      }}
    >
      <article className={clsx(styles.page, styles[variant])} data-app-page-container tabIndex={-1}>
        <header className={styles.header}>
          <button
            data-name="app_page:material_icon"
            type="button"
            className={styles.backButton}
            aria-label={t`Go back`}
            onClick={handleBack}
          >
            <MaterialIcon icon="arrow_back" className={styles.backIcon} />
          </button>
          {icon && <img src={icon} className={styles.icon} alt="" />}
          <DDSDisplayText as="h1" level="sm" className={styles.title}>
            {title}
          </DDSDisplayText>
        </header>
        {children}
      </article>
    </FocusTrap>
  );
};

export default AppPage;
